import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Overline, Reveal } from "../components/common";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { FAQS, IMAGES } from "../data/content";

export default function FAQ() {
  const { t } = useTranslation();
  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("faq.hero.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl">
              {t("faq.hero.titleLead")} <span className="text-brand-teal">{t("faq.hero.titleAccent")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-4xl lg:max-w-none lg:whitespace-nowrap">
              {t("faq.hero.subtitle")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Two-column: Sharing-is-not-caring card (left) + Accordion (right) */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-14 items-start">
            {/* Sharing-is-not-caring card — sticky on desktop */}
            <Reveal>
              <div
                data-testid="faq-sharing-card"
                className="lg:sticky lg:top-24"
              >
                <div className="relative">
                  <div className="absolute -top-6 -right-4 h-40 w-40 rounded-full bg-brand-teal/15 blur-2xl" />
                  <div className="absolute -bottom-8 -left-6 h-48 w-48 rounded-full bg-brand-navy/10 blur-2xl" />
                  <div className="relative rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
                    <div className="bg-brand-navy px-6 py-6 sm:px-8 sm:py-7">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal-light">
                        {t("faq.sharingCard.kicker")}
                      </p>
                      <p className="mt-2 text-xl sm:text-2xl font-heading font-semibold text-white whitespace-nowrap">
                        {t("faq.sharingCard.title")}
                      </p>
                      <p className="mt-2 text-white/70 leading-relaxed">
                        {t("faq.sharingCard.body")}
                      </p>
                    </div>
                    <div className="relative">
                      <img
                        src={IMAGES.hero}
                        alt="A person struggling with addiction — the community HepCURE reaches and supports"
                        className="w-full h-[300px] sm:h-[360px] object-cover object-center"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy via-brand-navy/25 to-brand-teal/50 mix-blend-soft-light" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 via-transparent to-transparent" />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Accordion */}
            <Reveal delay={0.1}>
              <Accordion
                type="single"
                collapsible
                className="w-full space-y-4"
                data-testid="faq-accordion"
              >
                {FAQS.map((item, i) => {
                  const loc = t("content.faqs", { returnObjects: true });
                  const merged = (Array.isArray(loc) && loc[i]) ? loc[i] : item;
                  return (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    data-testid={`faq-item-${i}`}
                    className="border border-gray-100 rounded-2xl px-6 bg-brand-offwhite/60 shadow-sm"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="text-left text-base sm:text-lg font-semibold font-heading text-brand-navy hover:no-underline py-5"
                    >
                      {merged.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-500 leading-relaxed pb-5 text-base">
                      {merged.a}
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-14 bg-brand-offwhite">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-navy">
              {t("faq.cta.title")}
            </h2>
            <p className="mt-3 text-slate-500">
              {t("faq.cta.body")}
            </p>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors"
            >
              {t("faq.cta.cta")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
